import { useState, useEffect, useMemo } from "react";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import {
  getValidationSchema,
  initialValues as initialValuesBase,
  FREQUENCY_OPTIONS,
} from "./config";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Grid2 as Grid,
  Button,
  CircularProgress,
  Typography,
  Divider,
} from "@mui/material";
import { green } from "@mui/material/colors";
import { Campaign } from "types/campaign";
import { useMutation, useQuery } from "@apollo/client";
import {
  ADD_CAMPAIGN,
  UPDATE_CAMPAIGN,
  ADD_CAMPAIGN_USERS,
  DELETE_CAMPAIGN_USERS,
  ADD_CAMPAIGN_SURVEYS,
  DELETE_CAMPAIGN_SURVEYS,
} from "graphql/campaign/campaign.mutation";
import { GET_SURVEYS_BY_ORG_ID, GET_SURVEYS } from "graphql/survey/survey.query";
import { GET_ORG_USERS_BY_ORG } from "graphql/organizationUser/organization_user.query";
import { GET_USERS } from "graphql/user/user.query";
import { GET_CAMPAIGNS, GET_CAMPAIGNS_BY_ORG_ID } from "graphql/campaign/campaign.query";
import { Modal } from "components/Modal";
import { NotificationSeverity } from "helpers/constants";
import { useNotifications } from "hooks/useNotifications";
import { ListWithEntitySelector } from "components/ListWithEntitySelector";
import { Survey } from "types/survey";
import { User } from "types/user";
import { StyledFormicError } from "../CreateOrEditTagModal/styles";
import { useParams } from "react-router-dom";

/** Format an ISO date string for datetime-local input (local timezone). */
const toLocalDatetimeString = (isoString: string): string => {
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export type Props = {
  createOrEditCampaignModal: boolean;
  handleCreateOrEditCampaignModalDismiss: () => void;
  handleCreateOrEditCampaignChange: () => void;
  campaign?: Campaign | null;
};

const CreateOrEditCampaignModal = ({
  createOrEditCampaignModal,
  handleCreateOrEditCampaignModalDismiss,
  handleCreateOrEditCampaignChange,
  campaign = null,
}: Props) => {
  const isEditMode = campaign !== null;
  const [loading, setLoading] = useState(false);
  const { notify } = useNotifications();
  const { organizationId } = useParams();
  
  // Get org_id from URL or campaign
  const orgId = organizationId || campaign?.org_id || null;

  // Get queries to refetch based on org context
  const refetchQueriesConfig = useMemo(() => {
    if (orgId) {
      return [
        {
          query: GET_CAMPAIGNS_BY_ORG_ID,
          variables: { orgId },
        },
      ];
    }
    return [{ query: GET_CAMPAIGNS }];
  }, [orgId]);

  const [addCampaign] = useMutation(ADD_CAMPAIGN, {
    refetchQueries: refetchQueriesConfig,
  });
  const [updateCampaign] = useMutation(UPDATE_CAMPAIGN, {
    refetchQueries: refetchQueriesConfig,
  });
  const [addCampaignUsers] = useMutation(ADD_CAMPAIGN_USERS, {
    refetchQueries: refetchQueriesConfig,
  });
  const [deleteCampaignUsers] = useMutation(DELETE_CAMPAIGN_USERS, {
    refetchQueries: refetchQueriesConfig,
  });
  const [addCampaignSurveys] = useMutation(ADD_CAMPAIGN_SURVEYS, {
    refetchQueries: refetchQueriesConfig,
  });
  const [deleteCampaignSurveys] = useMutation(DELETE_CAMPAIGN_SURVEYS, {
    refetchQueries: refetchQueriesConfig,
  });
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [selectedSurveys, setSelectedSurveys] = useState<Survey[]>([]);

  const { data: orgSurveysData } = useQuery(GET_SURVEYS_BY_ORG_ID, {
    variables: { orgId: orgId || "" },
    skip: !orgId,
  });
  const { data: allSurveysData } = useQuery(GET_SURVEYS, {
    skip: !!orgId, // Only fetch all surveys if no org_id
  });
  const { data: orgUsersData } = useQuery(GET_ORG_USERS_BY_ORG, {
    variables: { orgId: orgId || "" },
    skip: !orgId,
  });
  const { data: allUsersData } = useQuery(GET_USERS, {
    skip: !!orgId, // Only fetch all users if no org_id
  });

  const surveys = useMemo(() => {
    if (orgId) {
      return (orgSurveysData?.survey || []) as Survey[];
    } else {
      return (allSurveysData?.survey || []) as Survey[];
    }
  }, [orgId, orgSurveysData, allSurveysData]);

  const users = useMemo(() => {
    if (orgId) {
      // If org_id exists, use organization users
      if (!orgUsersData?.organization_user) return [];
      return (orgUsersData.organization_user.map((ou: any) => ou.user).filter(Boolean)) as User[];
    } else {
      // If no org_id, show all users
      return (allUsersData?.user || []) as User[];
    }
  }, [orgId, orgUsersData, allUsersData]);

  useEffect(() => {
    if (campaign) {
      // Build User[] including user_id from campaign_user (nested user fragment doesn't return user_id)
      const campaignUsers = (campaign.users || [])
        .map((cu) =>
          cu.user && cu.user_id
            ? { ...cu.user, user_id: cu.user_id }
            : null
        )
        .filter(Boolean) as User[];
      setSelectedUsers(campaignUsers);
      
      // Wait for surveys to load before setting
      if (surveys.length > 0) {
        const campaignSurveys = (campaign.surveys || [])
          .map((cs) => surveys.find((s) => s.survey_id === cs.survey_id))
          .filter(Boolean) as Survey[];
        setSelectedSurveys(campaignSurveys);
      }
    } else {
      // Reset to empty when creating new campaign
      setSelectedUsers([]);
      setSelectedSurveys([]);
    }
  }, [campaign, surveys]);

  // Sync selectedSurveys and selectedUsers with formik values when they change
  useEffect(() => {
    const surveyIds = selectedSurveys.map((s) => s.survey_id || "").filter(Boolean);
    if (JSON.stringify(formik.values.survey_ids) !== JSON.stringify(surveyIds)) {
      formik.setFieldValue("survey_ids", surveyIds, false);
    }
  }, [selectedSurveys]);

  useEffect(() => {
    const userIds = selectedUsers.map((u) => u.user_id || "").filter(Boolean);
    if (JSON.stringify(formik.values.user_ids) !== JSON.stringify(userIds)) {
      formik.setFieldValue("user_ids", userIds, false);
    }
  }, [selectedUsers]);

  const initialValues = useMemo(() => {
    if (campaign) {
      return {
        name: campaign.name ?? "",
        org_id: campaign.org_id || orgId || "",
        start_ts: campaign.start_ts ? toLocalDatetimeString(campaign.start_ts) : "",
        end_ts: campaign.end_ts ? toLocalDatetimeString(campaign.end_ts) : "",
        frequency: campaign.frequency || "",
        should_send_email: campaign.should_send_email || false,
        user_ids: campaign.users?.map((u) => u.user_id) || [],
        survey_ids: campaign.surveys?.map((s) => s.survey_id) || [],
      };
    }
    return {
      ...initialValuesBase,
      org_id: orgId || "",
    };
  }, [campaign, orgId]);

  const formik = useFormik({
    initialValues,
    validationSchema: getValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await handleCreateOrEditCampaign(values);
    },
  });

  const handleCreateOrEditCampaign = async (values: any) => {
    setLoading(true);

    try {
      const startTs = new Date(values.start_ts).toISOString();
      const endTs = new Date(values.end_ts).toISOString();

      if (isEditMode && campaign?.campaign_id) {
        // Update campaign
        await updateCampaign({
          variables: {
            campaign_id: campaign.campaign_id,
            name: values.name || null,
            audience_roles: [],
            end_ts: endTs,
            frequency: values.frequency,
            should_send_email: values.should_send_email,
            start_ts: startTs,
          },
        });

        // Update users
        await deleteCampaignUsers({
          variables: { campaign_id: campaign.campaign_id },
        });
        if (selectedUsers.length > 0) {
          const campaignUsersPayload = selectedUsers
            .filter((u) => u.user_id)
            .map((u) => ({
              campaign_id: campaign.campaign_id,
              user_id: u.user_id,
            }));
          if (campaignUsersPayload.length > 0) {
            await addCampaignUsers({
              variables: { campaign_users: campaignUsersPayload },
            });
          }
        }

        // Update surveys
        await deleteCampaignSurveys({
          variables: { campaign_id: campaign.campaign_id },
        });
        if (selectedSurveys.length > 0) {
          await addCampaignSurveys({
            variables: {
              campaign_surveys: selectedSurveys.map((s) => ({
                campaign_id: campaign.campaign_id,
                survey_id: s.survey_id,
              })),
            },
          });
        }

        notify({
          message: "Campaign updated successfully",
          severity: NotificationSeverity.success,
        });
      } else {
        // Create campaign
        // Determine org_id value: use orgId from URL, or form values, or null for admin campaigns
        const orgIdValue = orgId || (values.org_id && values.org_id.trim() !== "" ? values.org_id : null);
        
        const result = await addCampaign({
          variables: {
            name: values.name || null,
            audience_roles: [],
            end_ts: endTs,
            frequency: values.frequency,
            org_id: orgIdValue, // null for admin campaigns, uuid string for org campaigns
            should_send_email: values.should_send_email,
            start_ts: startTs,
          } as any, // Type assertion to handle nullable org_id
        });

        const campaignId = result.data?.insert_campaign?.returning?.[0]?.campaign_id;

        if (campaignId) {
          // Add users
          if (selectedUsers.length > 0) {
            const campaignUsersPayload = selectedUsers
              .filter((u) => u.user_id)
              .map((u) => ({
                campaign_id: campaignId,
                user_id: u.user_id,
              }));
            if (campaignUsersPayload.length > 0) {
              await addCampaignUsers({
                variables: { campaign_users: campaignUsersPayload },
              });
            }
          }

          // Add surveys
          if (selectedSurveys.length > 0) {
            await addCampaignSurveys({
              variables: {
                campaign_surveys: selectedSurveys.map((s) => ({
                  campaign_id: campaignId,
                  survey_id: s.survey_id,
                })),
              },
            });
          }

          notify({
            message: "Campaign created successfully",
            severity: NotificationSeverity.success,
          });
        }
      }

      handleCreateOrEditCampaignChange();
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      notify({
        message: error.message || `Error ${isEditMode ? "updating" : "creating"} campaign`,
        severity: NotificationSeverity.error,
      });
    }
  };


  return (
    <Modal
      open={createOrEditCampaignModal}
      onClose={handleCreateOrEditCampaignModalDismiss}
      title={isEditMode ? "Edit Campaign" : "Create Campaign"}
      size="large"
    >
      <Box sx={{ p: 4 }}>

        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  placeholder="Optional campaign name"
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Start Date & Time"
                  type="datetime-local"
                  name="start_ts"
                  value={formik.values.start_ts}
                  onChange={formik.handleChange}
                  error={formik.touched.start_ts && Boolean(formik.errors.start_ts)}
                  helperText={formik.touched.start_ts && formik.errors.start_ts}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="End Date & Time"
                  type="datetime-local"
                  name="end_ts"
                  value={formik.values.end_ts}
                  onChange={formik.handleChange}
                  error={formik.touched.end_ts && Boolean(formik.errors.end_ts)}
                  helperText={formik.touched.end_ts && formik.errors.end_ts}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="frequency-label">Frequency</InputLabel>
                  <Select
                    labelId="frequency-label"
                    id="frequency"
                    name="frequency"
                    value={formik.values.frequency}
                    onChange={formik.handleChange}
                    label="Frequency"
                    error={formik.touched.frequency && Boolean(formik.errors.frequency)}
                  >
                    {FREQUENCY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <ErrorMessage name="frequency" component={StyledFormicError} />
              </Grid>

              <Grid size={12}>
                <Divider sx={{ my: 2 }} />
                <ListWithEntitySelector
                  label="Users"
                  elements={selectedUsers}
                  onChangeElements={(users) => {
                    setSelectedUsers(users);
                    formik.setFieldValue(
                      "user_ids",
                      users.map((u) => u.user_id || "").filter(Boolean)
                    );
                  }}
                  idField="user_id"
                  entityName="User"
                  entityList={users}
                  displayField="name"
                  displayFieldSecondary="email"
                />
              </Grid>

              <Grid size={12}>
                <ListWithEntitySelector
                  label="Surveys"
                  elements={selectedSurveys}
                  onChangeElements={(surveys) => {
                    setSelectedSurveys(surveys);
                    formik.setFieldValue(
                      "survey_ids",
                      surveys.map((s) => s.survey_id || "").filter(Boolean)
                    );
                    formik.setFieldTouched("survey_ids", true);
                  }}
                  idField="survey_id"
                  entityName="Survey"
                  entityList={surveys}
                  displayField="name"
                />
                {formik.touched.survey_ids && formik.errors.survey_ids && (
                  <StyledFormicError>
                    {String(formik.errors.survey_ids)}
                  </StyledFormicError>
                )}
              </Grid>

              <Grid size={12}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCreateOrEditCampaignModalDismiss}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                  >
                    {isEditMode ? "Update" : "Create"}
                  </Button>
                  {loading && (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: green[500],
                        position: "absolute",
                      }}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormikProvider>
      </Box>
    </Modal>
  );
};

export default CreateOrEditCampaignModal;
